import {useCallback, useRef, useState} from 'react';
import base64ToUint8Array from '@contentlab/utils/base64';
import {createWavBlobFromPcmBytes, hasWavHeader} from '@contentlab/utils/wav';

interface StreamingAudioControls {
  play: (url: string, options?: RequestInit) => Promise<void>;
  stop: () => void;
}

interface StreamingAudioState {
  isLoading: boolean;
  error: string | null;
  isPlaying: boolean;
  audioUrl: string | null;
  mimeType: string | null;
  duration: number;
}

interface StreamingAudioOptions {
  onPlayStart?: () => void;
  onPlayEnd?: () => void;
  onAudioReady?: (audioUrl: string, mimeType: string) => void;
  abortOnStop?: boolean;
  bufferChunks?: number;
  volume?: number;
}

/**
 * A React hook for streaming audio playback from any SSE endpoint.
 *
 * This hook enables real-time audio streaming from any Server-Sent Events endpoint,
 * playing audio chunks as they arrive from the server for immediate playback without
 * waiting for the complete audio.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const streamingAudio = useStreamingAudio({
 *     onPlayStart: () => console.log('Audio started playing'),
 *     onPlayEnd: () => console.log('Audio finished playing'),
 *     abortOnStop: false, // Allow streaming to continue even when stopped
 *     bufferChunks: 3 // Wait for 3 chunks before starting playback
 *   });
 *
 *   const handlePlay = () => {
 *     streamingAudio.play('https://api.example.com/audio/stream', {
 *       method: 'POST',
 *       headers: {
 *         'Accept': 'text/event-stream',
 *         'Content-Type': 'application/json',
 *       },
 *       body: JSON.stringify({ text: 'Hello world' })
 *     });
 *   };
 *
 *   return (
 *     <div>
 *       <button
 *         onClick={handlePlay}
 *         disabled={streamingAudio.isLoading}
 *       >
 *         {streamingAudio.isPlaying ? 'Stop' : 'Play Audio'}
 *       </button>
 *       {streamingAudio.error && <p>Error: {streamingAudio.error}</p>}
 *     </div>
 *   );
 * }
 * ```
 *
 * @param options - Optional configuration object
 * @param options.onPlayStart - Callback fired when the first audio chunk starts playing
 * @param options.onPlayEnd - Callback fired when all audio chunks finish playing
 * @param options.onAudioReady - Callback fired with audio URL and MIME type when streaming completes
 * @param options.abortOnStop - Whether to abort the streaming request when stop() is called (default: true)
 * @param options.bufferChunks - Number of chunks to load before starting playback (default: 1). If total chunks < bufferChunks, playback will start when the stream completes.
 * @returns An object containing:
 * - `isLoading`: Whether the hook is currently loading/streaming audio
 * - `error`: Any error message from the streaming process
 * - `isPlaying`: Whether audio is currently playing
 * - `audioUrl`: The blob URL of the audio (available after streaming completes, for caching)
 * - `mimeType`: The MIME type of the audio (available after streaming completes)
 * - `duration`: Total duration of the audio in seconds (accumulated from all chunks)
 * - `play`: Function to start streaming and playing audio from any URL
 * - `stop`: Function to stop current playback and cleanup resources
 */
export default function useStreamingAudio(
  options: StreamingAudioOptions = {}
): StreamingAudioState & StreamingAudioControls {
  const {abortOnStop = true, bufferChunks = 1, volume = 1} = options;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);

  const contextRef = useRef<AudioContext | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const volumeRef = useRef<number>(volume);
  const timeRef = useRef(0);
  const activeNodesRef = useRef(0);
  const audioChunksRef = useRef<Uint8Array[]>([]);
  const sourceNodesRef = useRef<AudioBufferSourceNode[]>([]);
  const loadedChunksRef = useRef(0);
  const bufferedChunksRef = useRef<Uint8Array[]>([]);
  const playbackStartedRef = useRef(false);

  // Update volume ref when it changes
  volumeRef.current = volume;

  /**
   * Stops current audio playback and cleans up all resources.
   *
   * This function:
   * - Closes the Web Audio context
   * - Aborts any ongoing fetch requests (if abortOnStop is true)
   * - Resets all internal state
   * - Updates UI state to reflect stopped status
   */
  const stop = useCallback(() => {
    contextRef.current?.close();
    if (abortOnStop) {
      controllerRef.current?.abort();
      audioChunksRef.current = [];
      controllerRef.current = null;
    }
    sourceNodesRef.current.forEach((node) => node.disconnect());
    gainNodeRef.current?.disconnect();
    contextRef.current = null;
    gainNodeRef.current = null;
    timeRef.current = 0;
    activeNodesRef.current = 0;
    sourceNodesRef.current = [];
    loadedChunksRef.current = 0;
    bufferedChunksRef.current = [];
    playbackStartedRef.current = false;
    setIsPlaying(false);
    setIsLoading(false);
    setAudioUrl(null);
    setMimeType(null);
    setDuration(0);
  }, [abortOnStop]);

  /**
   * Processes and plays a single audio chunk from the stream.
   *
   * This function:
   * - Converts raw audio data to a proper WAV blob
   * - Decodes the audio using Web Audio API
   * - Schedules playback to start immediately (first chunk) or after previous chunks
   * - Tracks when the chunk finishes playing to update UI state
   *
   * @param chunk - Raw audio data as Uint8Array from the server
   */
  const playChunk = async (chunk: Uint8Array) => {
    if (!contextRef.current) return;

    // Create audio blob (add WAV header if needed)
    const blob = hasWavHeader(chunk)
      ? new Blob([new Uint8Array(chunk)], {type: 'audio/wav'})
      : createWavBlobFromPcmBytes(chunk);

    // Decode audio data
    const arrayBuffer = await blob.arrayBuffer();
    if (!contextRef.current) return;

    const audioBuffer = await contextRef.current.decodeAudioData(arrayBuffer);
    if (!contextRef.current) return;

    const chunkDuration = audioBuffer.duration;
    loadedChunksRef.current += 1;

    // Update duration even if not playing yet
    setDuration((prev) => prev + chunkDuration);

    // If playback hasn't started yet, buffer the chunk
    if (!playbackStartedRef.current) {
      bufferedChunksRef.current.push(chunk);

      // Check if we have enough chunks to start playback
      if (loadedChunksRef.current >= bufferChunks) {
        playbackStartedRef.current = true;
        setIsPlaying(true);
        setIsLoading(false);
        options.onPlayStart?.();

        // Play all buffered chunks in sequence
        // eslint-disable-next-line no-restricted-syntax
        for await (const bufferedChunk of bufferedChunksRef.current) {
          await playBufferedChunk(bufferedChunk);
        }
        bufferedChunksRef.current = [];
      }
    } else {
      // Playback has started, play this chunk immediately
      await playBufferedChunk(chunk);
    }
  };

  /**
   * Starts playback if we have buffered chunks but haven't started yet.
   * This handles the edge case where total chunks < bufferChunks.
   */
  const startPlaybackIfReady = useCallback(() => {
    if (!playbackStartedRef.current && bufferedChunksRef.current.length > 0) {
      playbackStartedRef.current = true;
      setIsPlaying(true);
      setIsLoading(false);
      options.onPlayStart?.();

      // Play all buffered chunks in sequence
      (async () => {
        // eslint-disable-next-line no-restricted-syntax
        for await (const bufferedChunk of bufferedChunksRef.current) {
          await playBufferedChunk(bufferedChunk);
        }
        bufferedChunksRef.current = [];
      })();
    }
  }, [options]);

  const playBufferedChunk = async (chunk: Uint8Array) => {
    if (!contextRef.current) return;

    // Create audio blob (add WAV header if needed)
    const blob = hasWavHeader(chunk)
      ? new Blob([new Uint8Array(chunk)], {type: 'audio/wav'})
      : createWavBlobFromPcmBytes(chunk);

    // Decode audio data
    const arrayBuffer = await blob.arrayBuffer();
    if (!contextRef.current) return;

    const audioBuffer = await contextRef.current.decodeAudioData(arrayBuffer);
    if (!contextRef.current) return;

    // Create and configure audio source
    const source = contextRef.current.createBufferSource();
    source.buffer = audioBuffer;

    // Connect through gain node for volume control
    // Update gain node volume to current volume with smooth transition
    if (gainNodeRef.current && contextRef.current) {
      gainNodeRef.current.gain.linearRampToValueAtTime(
        volumeRef.current,
        contextRef.current.currentTime + 0.05
      );
      source.connect(gainNodeRef.current);
    } else {
      // Fallback: connect directly if gain node doesn't exist
      source.connect(contextRef.current.destination);
    }

    // Track this source node
    sourceNodesRef.current.push(source);
    const chunkDuration = audioBuffer.duration;

    // Start playing immediately if this is the first chunk to play
    if (timeRef.current === 0) {
      timeRef.current = contextRef.current.currentTime;
    }

    // Schedule this chunk to play after previous chunks
    source.start(timeRef.current);
    timeRef.current += chunkDuration;

    // Track when this chunk finishes playing
    activeNodesRef.current += 1;
    source.onended = () => {
      activeNodesRef.current -= 1;
      // Remove from sourceNodesRef
      const index = sourceNodesRef.current.indexOf(source);
      if (index > -1) {
        sourceNodesRef.current.splice(index, 1);
      }
      if (activeNodesRef.current === 0) {
        setIsPlaying(false);
        options.onPlayEnd?.();
      }
    };
  };

  /**
   * Starts streaming and playing audio from any SSE endpoint.
   *
   * This function:
   * - Stops any existing playback
   * - Initializes Web Audio context
   * - Makes a fetch request to the provided URL
   * - Parses Server-Sent Events (SSE) stream for audio data
   * - Plays each audio chunk as it arrives for immediate playback
   * - Handles stream completion and error states
   *
   * @param url - The URL to fetch from
   * @param fetchOptions - Optional fetch options (method, headers, body, etc.)
   * @throws Will set error state if streaming fails or server returns error
   */
  const play = useCallback(
    async (url: string, fetchOptions?: RequestInit) => {
      // Save reference to previous context before stopping
      const previousContext = contextRef.current;
      stop();

      // Wait for previous AudioContext to fully close before creating new one
      // This prevents browser issues with multiple AudioContexts
      if (previousContext && previousContext.state !== 'closed') {
        try {
          await previousContext.close();
        } catch {
          // Ignore close errors
        }
      }

      // Always abort any previous streaming request to prevent mixing
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Reset state for new playback session
      audioChunksRef.current = [];
      loadedChunksRef.current = 0;
      bufferedChunksRef.current = [];
      playbackStartedRef.current = false;

      setIsLoading(true);
      setError(null);

      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        // Initialize audio context
        contextRef.current = new (window.AudioContext ||
          (window as any).webkitAudioContext)();

        // Create gain node for volume control
        gainNodeRef.current = contextRef.current.createGain();
        gainNodeRef.current.gain.value = volumeRef.current;
        gainNodeRef.current.connect(contextRef.current.destination);

        // Ensure AudioContext is running (not suspended)
        // Browser autoplay policies might start it in suspended state
        if (contextRef.current.state === 'suspended') {
          await contextRef.current.resume();
        }

        // Start streaming request
        const response = await fetch(url, {
          signal: controller.signal,
          ...fetchOptions,
        });

        if (!response.ok || !response.body) {
          throw new Error(`Stream failed: ${response.status}`);
        }

        // Parse SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const {done, value} = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, {stream: true});
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line

          // eslint-disable-next-line no-restricted-syntax
          for (const line of lines) {
            const trimmed = line.trim();

            if (trimmed.startsWith('data: ')) {
              // Parse audio data
              const jsonStr = trimmed
                .slice(6)
                .replace(/'/g, '"')
                .replace(/None/g, 'null');
              const data = JSON.parse(jsonStr);
              if (data?.audio) {
                const chunk = base64ToUint8Array(data.audio);
                audioChunksRef.current.push(chunk);
                // eslint-disable-next-line no-await-in-loop
                await playChunk(chunk);
              }
            } else if (trimmed.startsWith('event: complete')) {
              // Start playback if we have buffered chunks but haven't started yet
              // This handles the edge case where total chunks < bufferChunks
              startPlaybackIfReady();

              // Combine all chunks and create blob URL for caching
              if (audioChunksRef.current.length > 0) {
                const totalLength = audioChunksRef.current.reduce(
                  (sum, chunk) => sum + chunk.length,
                  0
                );
                const combined = new Uint8Array(totalLength);
                let offset = 0;
                // eslint-disable-next-line no-restricted-syntax
                for (const chunk of audioChunksRef.current) {
                  combined.set(chunk, offset);
                  offset += chunk.length;
                }

                // Create blob from combined chunks
                const hasHeader =
                  combined[0] === 0x52 &&
                  combined[1] === 0x49 &&
                  combined[2] === 0x46 &&
                  combined[3] === 0x46;
                const blob = hasHeader
                  ? new Blob([combined], {type: 'audio/wav'})
                  : createWavBlobFromPcmBytes(combined);

                const objectUrl = URL.createObjectURL(blob);
                setAudioUrl(objectUrl);
                setMimeType(blob.type);
                options.onAudioReady?.(objectUrl, blob.type);
              }
              break;
            } else if (trimmed.startsWith('event: error')) {
              throw new Error('Stream error');
            }
          }
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err.message);
        }
        stop();
      }
    },
    [stop, startPlaybackIfReady]
  );

  return {
    isLoading,
    error,
    isPlaying,
    audioUrl,
    mimeType,
    duration,
    play,
    stop,
  };
}
